import { useTheme } from "@emotion/react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import type { SearchBarProps } from "@aviary";
import {
  Dropdown,
  DropdownContent,
  DropdownItem,
  DropdownTrigger,
  SearchBar,
  Skeleton,
  SkeletonList,
} from "@aviary";
import type { PractitionerTypeFragment } from "@shared/data/fragments/PractitionerTypeFragment/PractitionerTypeFragment.fragment";
import { useDebounce } from "@shared/hooks/useDebounce/useDebounce";
import { useKeyboardNavigationList } from "@shared/hooks/useKeyboardNavigationList/useKeyboardNavigationList";
import { useOutsideClick } from "@shared/hooks/useOutsideClick/useOutsideClick";
import { l } from "@unauthenticated/shared/locales/i18n";

import { useDesignations } from "./useDesignations";

import * as styles from "./DesignationSearch.styles";

interface Props extends Pick<SearchBarProps, "onBlur"> {
  value?: PractitionerTypeFragment;
  setValue: (value: PractitionerTypeFragment | null) => void;
  required?: boolean;
  hasErrors?: boolean;
}

const RESULTS_TO_SHOW = 6;

const DesignationSearch = ({ value, hasErrors = false, setValue, ...rest }: Props) => {
  const { t } = useTranslation();
  const { designations, loading } = useDesignations();
  const initialDesignation = designations?.find(element => element.id === value?.id);
  const [searchText, setSearchText] = useState("");
  const [searchBarFocus, setSearchBarFocus] = useState(false);
  const [searchResults, setSearchResults] = useState<PractitionerTypeFragment[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const validationInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();

  useEffect(() => {
    if (initialDesignation) {
      setSearchText(initialDesignation?.description);
    }
  }, [initialDesignation]);

  useEffect(() => {
    if (validationInputRef.current) {
      if (rest.required && !value?.id) {
        validationInputRef.current.setCustomValidity(t(l.common.pleaseSelectDesignation));
      } else {
        validationInputRef.current.setCustomValidity("");
      }
    }
  }, [value, t, rest.required]);

  const clearField = (e?: KeyboardEvent<Element> | MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();
    setSearchText("");
    setValue(null);
  };

  useOutsideClick(dropdownRef, () => {
    if (isDropdownOpen) {
      setIsDropdownOpen(false);
    }
  });

  const onEnter = () => {
    if (searchText === "" || searchResults.length === 0) return;
    const pracType = searchResults[itemSelectedIndex];

    if (!pracType) return;

    setSearchText(pracType.description);
    setValue(pracType);
  };

  const { itemSelectedIndex, onKeyDown } = useKeyboardNavigationList(
    RESULTS_TO_SHOW,
    null,
    null,
    onEnter,
    null,
    clearField
  );

  const renderItem = (item: PractitionerTypeFragment) => (
    <DropdownItem
      key={item.id}
      onSelect={() => {
        setSearchText(item.description);
        setValue(item);
        setIsDropdownOpen(false);
      }}
    >
      {item.description}
    </DropdownItem>
  );

  const renderItems = () => {
    const items = searchText === "" ? designations : searchResults;
    if (!items?.length) {
      return <DropdownItem disabled>{t(l.common.DesignationNoResults)}</DropdownItem>;
    }

    return items.map(renderItem);
  };

  const renderSkeletons = (numOfSkeletons: number): ReactNode => (
    <SkeletonList number={numOfSkeletons}>
      <Skeleton type="typography" typographySize="body" margin="top" isFullWidth />
    </SkeletonList>
  );

  const renderSearchResults = () => {
    if (loading || !designations) {
      return (
        <DropdownContent>
          <div>{renderSkeletons(RESULTS_TO_SHOW)}</div>
        </DropdownContent>
      );
    }

    return <DropdownContent>{renderItems()}</DropdownContent>;
  };

  const debounceOnSearch = useDebounce(filteredResults => setSearchResults(filteredResults), 300);

  const handleSearch = (searchValue: string) => {
    const searchValueLowerCase = searchValue.toLocaleLowerCase();

    const isDesignation = designations?.some(
      designation => designation.description.toLocaleLowerCase() === searchValueLowerCase
    );

    if (searchValue === "" || !isDesignation) {
      setValue(null);
    }

    const filteredResults = designations?.filter(designation => {
      if (searchValue) {
        return designation.description.toLocaleLowerCase().includes(searchValueLowerCase);
      }

      return true;
    });
    setIsDropdownOpen(true);
    setSearchBarFocus(true);
    setSearchText(searchValue);
    debounceOnSearch(filteredResults);
  };

  return (
    <div css={styles.dropDown} ref={dropdownRef} style={{ position: "relative" }}>
      <input
        type="text"
        name="practitionerTypeId"
        value={value?.id || ""}
        required={rest.required}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0,
          pointerEvents: "none",
          width: "100%",
          height: "100%",
          zIndex: 10,
        }}
        tabIndex={-1}
        ref={ref => {
          validationInputRef.current = ref;
        }}
      />
      <Dropdown
        isOpen={isDropdownOpen}
        triggerCallback={() => setIsDropdownOpen}
        customDropdownHeight={11}
        isFullWidth
      >
        <DropdownTrigger>
          <SearchBar
            incrementalSearch
            boxStyles={styles.ds4SearchBox(theme, hasErrors)}
            inputStyles={styles.searchBarInput(theme)}
            value={searchText}
            isFocus={searchBarFocus}
            onSearch={() => null}
            onChange={e => handleSearch(e.target.value)}
            onClick={() => setIsDropdownOpen(true)}
            placeholder={t(l.common.DesignationSearch)}
            onTrailingIconClick={clearField}
            onKeyDown={onKeyDown}
            {...rest}
          />
        </DropdownTrigger>
        {renderSearchResults()}
      </Dropdown>
    </div>
  );
};

export { DesignationSearch };
